class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.touchStartX = 0;
    this.touchStartY = 0;
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.addEventListener('touchmove', this.onTouchMove, { passive: false });
    this.addEventListener('click', (event) => {
      if (event.target.closest('.drawer__inner')) return;
      this.close();
    });
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  onTouchStart(event) {
    const touch = event.touches[0];
    if (!touch) return;
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
  }

  onTouchMove(event) {
    const touch = event.touches[0];
    if (!touch || event.touches.length !== 1) return;

    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (Math.abs(deltaX) <= Math.abs(deltaY)) return;

    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (target?.closest('.cart-drawer-upsell__track')) return;

    event.preventDefault();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.querySelector('free-shipping-bar')?.animateProgress?.();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement =
          this.querySelector('.drawer__close') ||
          this.querySelector('.cart-drawer__continue-btn') ||
          this.querySelector('.drawer__inner');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.classList.remove('is-empty');
    this.querySelector('.drawer__inner')?.classList.remove('is-empty');
    this.productId = parsedState.id;

    const drawerHtml = parsedState.sections?.['cart-drawer'];
    if (drawerHtml && this.querySelector('#CartDrawer-CartItems')) {
      this.replaceDrawerParts(drawerHtml);
    } else {
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });
    }

    const iconHtml = parsedState.sections?.['cart-icon-bubble'];
    const iconEl = document.getElementById('cart-icon-bubble');
    if (iconEl && iconHtml) {
      iconEl.innerHTML = this.getSectionInnerHTML(iconHtml) || iconHtml;
    }

    setTimeout(() => this.open());
  }

  replaceDrawerParts(drawerHtml) {
    const doc = new DOMParser().parseFromString(drawerHtml, 'text/html');

    const replaceInner = (selector) => {
      const current = this.querySelector(selector) || document.querySelector(selector);
      const next = doc.querySelector(selector);
      if (current && next) current.innerHTML = next.innerHTML;
    };

    replaceInner('.cart-drawer__footer');

    const tbody = this.querySelector('.cart-items tbody');
    const nextTbody = doc.querySelector('.cart-items tbody');
    if (!tbody || !nextTbody) {
      replaceInner('#CartDrawer-CartItems');
    } else {
      this.mergeCartRows(tbody, nextTbody);
    }

    const headingCount = this.querySelector('.drawer__heading-count');
    const nextCount = doc.querySelector('.drawer__heading-count');
    if (headingCount && nextCount) headingCount.textContent = nextCount.textContent;

    const headingHidden = this.querySelector('.drawer__heading .visually-hidden');
    const nextHidden = doc.querySelector('.drawer__heading .visually-hidden');
    if (headingHidden && nextHidden) headingHidden.textContent = nextHidden.textContent;

    const fsb = this.querySelector('free-shipping-bar');
    const nextFsb = doc.querySelector('free-shipping-bar');
    if (fsb && nextFsb) {
      fsb.replaceWith(nextFsb);
    } else if (!fsb && nextFsb) {
      this.querySelector('.drawer__header')?.after(nextFsb);
    } else if (fsb && !nextFsb) {
      fsb.remove();
    }

    const emptyEl = this.querySelector('.drawer__inner-empty');
    if (emptyEl) emptyEl.hidden = true;

    const itemsEl = this.querySelector('cart-drawer-items');
    itemsEl?.classList.remove('is-empty');

    const upsell = this.querySelector('cart-drawer-upsell');
    const nextUpsell = doc.querySelector('cart-drawer-upsell');
    if (!upsell && nextUpsell && itemsEl) {
      itemsEl.appendChild(nextUpsell);
    } else if (upsell && nextUpsell && upsell.dataset.productId !== nextUpsell.dataset.productId) {
      upsell.dataset.productId = nextUpsell.dataset.productId;
      upsell.loadRecommendations?.();
    }

    const cartTotal = doc.querySelector('cart-drawer')?.dataset.cartTotal;
    const itemCount = doc.querySelector('cart-drawer')?.dataset.itemCount;
    if (cartTotal != null) this.dataset.cartTotal = cartTotal;
    if (itemCount != null) this.dataset.itemCount = itemCount;
  }

  mergeCartRows(tbody, nextTbody) {
    const existing = new Map();
    tbody.querySelectorAll('.cart-item').forEach((row) => {
      const key = row.querySelector('[data-quantity-line-key]')?.dataset.quantityLineKey;
      if (key) existing.set(key, row);
    });

    const seen = new Set();
    const nextRows = [...nextTbody.querySelectorAll('.cart-item')];
    nextRows.forEach((row, index) => {
      const key = row.querySelector('[data-quantity-line-key]')?.dataset.quantityLineKey;
      if (!key) return;
      seen.add(key);

      if (existing.has(key)) {
        const current = existing.get(key);
        const currentInput = current.querySelector('.quantity__input');
        const nextInput = row.querySelector('.quantity__input');
        if (currentInput && nextInput) {
          currentInput.value = nextInput.value;
          currentInput.setAttribute('value', nextInput.value);
        }
        current.classList.toggle('cart-item--qty-one', parseInt(currentInput?.value, 10) === 1);
        if (row.dataset.linePrice) current.dataset.linePrice = row.dataset.linePrice;
        if (row.dataset.unitPrice) current.dataset.unitPrice = row.dataset.unitPrice;

        const nextPrice = row.querySelector('.cart-item__price:not(.cart-item__price--compare)');
        const currentPrice = current.querySelector('.cart-item__price:not(.cart-item__price--compare)');
        if (nextPrice && currentPrice) currentPrice.textContent = nextPrice.textContent;
      } else {
        const ref = tbody.querySelectorAll('.cart-item')[index];
        if (ref) tbody.insertBefore(row, ref);
        else tbody.appendChild(row);
      }
    });

    existing.forEach((row, key) => {
      if (!seen.has(key)) row.remove();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  shouldFetchSections() {
    return false;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.cart-drawer__footer',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  formatMoney(cents) {
    const format =
      this.closest('cart-drawer')?.dataset.moneyFormat || window.shopMoneyFormat || '${{amount}}';

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format).replace(/<[^>]*>/g, '').trim();
    }

    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';

    const formatWithDelimiters = (number, precision, thousands, decimal) => {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (Number(number) / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    };

    switch (formatString.match(placeholderRegex)?.[1]) {
      case 'amount':
        return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 2));
      case 'amount_no_decimals':
        return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 0));
      case 'amount_with_comma_separator':
        return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 2, '.', ','));
      case 'amount_no_decimals_with_comma_separator':
        return formatString.replace(placeholderRegex, formatWithDelimiters(cents, 0, '.', ','));
      default:
        return formatString.replace(placeholderRegex, (Number(cents) / 100).toFixed(2));
    }
  }

  applyOptimisticCartUpdate(line, quantity) {
    const row = this.querySelector(`#CartDrawer-Item-${line}`);
    const input = this.querySelector(`#Drawer-quantity-${line}`);
    if (!row) return;

    const previousQty = parseInt(input?.getAttribute('value'), 10) || parseInt(input?.value, 10) || 0;
    row.classList.toggle('cart-item--qty-one', quantity === 1);
    row.classList.toggle('is-removing', quantity === 0);

    const unitPrice = Number(row.dataset.unitPrice);
    const previousLinePrice = Number(row.dataset.linePrice);
    const nextLinePrice = quantity > 0 && Number.isFinite(unitPrice) ? unitPrice * quantity : 0;
    if (Number.isFinite(unitPrice)) row.dataset.linePrice = String(nextLinePrice);

    const drawer = this.closest('cart-drawer');
    if (!drawer) return;

    const previousTotal = Number(drawer.dataset.cartTotal);
    if (Number.isFinite(previousTotal) && Number.isFinite(previousLinePrice)) {
      const nextTotal = Math.max(0, previousTotal - previousLinePrice + nextLinePrice);
      drawer.dataset.cartTotal = String(nextTotal);
      this.updateSubtotalDisplay(nextTotal, drawer);
    }

    const previousCount = Number(drawer.dataset.itemCount);
    if (Number.isFinite(previousCount)) {
      const nextCount = Math.max(0, previousCount - previousQty + quantity);
      drawer.dataset.itemCount = String(nextCount);
      this.updateCartCountDisplay(nextCount);
    }
  }

  patchCartFromJson(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;

    const rows = [...this.querySelectorAll('.cart-item')];
    const rowByKey = new Map();
    for (const row of rows) {
      const key = row.querySelector('[data-quantity-line-key]')?.dataset.quantityLineKey;
      if (key) rowByKey.set(key, row);
    }

    const hasMissingRow = cart.items.some((item) => !rowByKey.has(item.key));
    if (hasMissingRow && cart.item_count > 0) return false;

    for (const [key, row] of rowByKey) {
      if (!cart.items.some((item) => item.key === key)) {
        row.remove();
      }
    }

    for (const item of cart.items) {
      this.patchCartRow(rowByKey.get(item.key), item);
    }

    this.patchCartDrawerMeta(cart);
    return true;
  }

  patchCartRow(row, item) {
    if (!row || !item) return;

    const input = row.querySelector('.quantity__input');
    if (input) {
      input.value = item.quantity;
      input.setAttribute('value', item.quantity);
    }

    row.classList.toggle('cart-item--qty-one', item.quantity === 1);
    row.classList.remove('is-updating', 'is-removing');
    row.dataset.linePrice = String(item.final_line_price);
    row.dataset.unitPrice = String(item.final_price);

    const priceEl = row.querySelector('.cart-item__price:not(.cart-item__price--compare)');
    if (priceEl) priceEl.textContent = this.formatMoney(item.final_price);

    row.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.add('hidden'));
  }

  patchCartDrawerMeta(cart) {
    const drawer = this.closest('cart-drawer') || document.querySelector('cart-drawer');
    const isEmpty = cart.item_count === 0;

    this.classList.toggle('is-empty', isEmpty);
    drawer?.classList.toggle('is-empty', isEmpty);
    drawer?.querySelector('.drawer__inner')?.classList.toggle('is-empty', isEmpty);

    if (drawer) {
      drawer.dataset.cartTotal = String(cart.total_price);
      drawer.dataset.itemCount = String(cart.item_count);
    }

    const emptyEl = drawer?.querySelector('.drawer__inner-empty');
    if (emptyEl) emptyEl.hidden = !isEmpty;

    const checkout = document.getElementById('CartDrawer-Checkout');
    if (checkout) checkout.disabled = isEmpty;

    this.updateSubtotalDisplay(cart.total_price, drawer, cart.original_total_price);
    this.updateCartCountDisplay(cart.item_count);
  }

  updateSubtotalDisplay(totalCents, drawer = this.closest('cart-drawer'), originalTotal) {
    const priceEl = drawer?.querySelector('[data-cart-subtotal]') || document.querySelector('[data-cart-subtotal]');
    if (!priceEl || !Number.isFinite(Number(totalCents))) return;

    priceEl.textContent = this.formatMoney(totalCents);
    priceEl.dataset.cents = String(totalCents);

    const compareEl = drawer?.querySelector('.cart-drawer__subtotal-compare');
    if (Number.isFinite(Number(originalTotal)) && originalTotal > totalCents) {
      if (compareEl) compareEl.textContent = this.formatMoney(originalTotal);
    } else if (compareEl && originalTotal != null) {
      compareEl.remove();
    }
  }

  updateCartCountDisplay(count) {
    const headingCount = document.querySelector('.drawer__heading-count');
    if (headingCount) headingCount.textContent = `(${count})`;

    const headingHidden = document.querySelector('.drawer__heading .visually-hidden');
    if (headingHidden) headingHidden.textContent = headingHidden.textContent.replace(/\d+/, String(count));

    const icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;

    let bubble = icon.querySelector('.cart-count-bubble');
    if (count <= 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML =
        '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      icon.appendChild(bubble);
    }

    const visible = bubble.querySelector('[aria-hidden="true"]');
    if (visible) visible.textContent = count < 100 ? String(count) : '';
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerUpsell extends HTMLElement {
  connectedCallback() {
    this.loadRecommendations();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
  }

  loadRecommendations() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const baseUrl = this.dataset.url;

    if (!productId || !sectionId || !baseUrl) {
      this.hidden = true;
      return;
    }

    fetch(`${baseUrl}&product_id=${productId}&section_id=${sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const upsell = html.querySelector('.cart-drawer-upsell');

        if (!upsell) {
          this.replaceChildren();
          this.classList.remove('is-loaded');
          this.hidden = true;
          return;
        }

        this.hidden = false;
        this.replaceChildren(upsell);
        this.classList.add('is-loaded');
        this.bindInteractions();
      })
      .catch((error) => {
        console.error(error);
        this.hidden = true;
      });
  }

  bindInteractions() {
    this.track = this.querySelector('.cart-drawer-upsell__track');
    this.prevBtn = this.querySelector('.cart-drawer-upsell__nav-btn--prev');
    this.nextBtn = this.querySelector('.cart-drawer-upsell__nav-btn--next');

    this.onScroll = () => this.updateNavState();
    this.track?.addEventListener('scroll', this.onScroll, { passive: true });

    this.prevBtn?.addEventListener('click', () => this.scrollBySlide(-1));
    this.nextBtn?.addEventListener('click', () => this.scrollBySlide(1));

    this.querySelectorAll('.cart-drawer-upsell__add').forEach((button) => {
      button.addEventListener('click', (event) => this.onAdd(event));
    });

    this.updateNavState();
  }

  scrollBySlide(direction) {
    if (!this.track) return;
    const slide = this.track.querySelector('.cart-drawer-upsell__slide');
    const amount = (slide?.offsetWidth || this.track.clientWidth * 0.85) + 12;
    this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }

  updateNavState() {
    if (!this.track || !this.prevBtn || !this.nextBtn) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const left = this.track.scrollLeft;

    this.prevBtn.disabled = left <= 2;
    this.nextBtn.disabled = left >= maxScroll - 2;
  }

  onAdd(event) {
    const button = event.currentTarget;
    const variantId = button?.dataset?.variantId;
    if (!variantId || button.disabled || button.classList.contains('is-loading')) return;

    const cart = document.querySelector('cart-drawer');
    if (!cart) return;

    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append(
      'sections',
      cart.getSectionsToRender().map((section) => section.id)
    );
    formData.append('sections_url', window.location.pathname);
    config.body = formData;

    cart.setActiveElement(button);

    fetch(`${routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error(response.description || response.message);
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-upsell',
          productVariantId: variantId,
          cartData: response,
        });

        cart.classList.remove('is-empty');
        cart.renderContents(response);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      });
  }
}

customElements.define('cart-drawer-upsell', CartDrawerUpsell);
